import { useMsal } from "@azure/msal-react";
import {
    Checkbox,
    DefaultButton,
    Label,
    Link,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import Excel from 'assets/SVGPack/excel.svg';
import { EControlType } from "components/ControlWrapper";
import CustomPanel from "components/controls/CustomPanel";
import FileImportDialog from "components/controls/FileImportDialog";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { saveAs } from 'file-saver';
import { EViewType, createGroupNumber, emitCustomEvent, fadeIn, friendlyValue, friendlyValueToFlexibleFieldValue, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateGroupStructure } from "global/Reducer/dataSlice";
import { ArrayToArrayByPredicateFlat, cyrb53, formatDate, parseDate } from "global/Utility/TSFunctions";
import * as IXrmTypes from 'model/CRM';
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import ValidationDialog from "views/BlueRelationship/ValidationDialog";
import { EValidationState, IValidationModel } from "views/BlueRelationship/ValidationDialog/ValidationDialog.type";
import ValidationItem from "views/BlueRelationship/ValidationDialog/ValidationItem";
import { EView } from "../EView.type";
import { EStatus } from "./EStatus";
import { IGroupStructureImportWizardProps } from "./IGroupStructureImportWizardProps.type";


interface IFeedback {
    status: EStatus;
    messages?: [];
}


interface IApi {
    '$type': "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal" |
    "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal" |
    "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib" |
    "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal" |
    null

}

interface IRowApi extends IApi {
    [key: string]: string;
    '$type': "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib";
}

interface IReadSheetRequestApi extends IApi {
    '$type': "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal";
    'comment': string;
    'IsFirstRowHeader': boolean;
    'sheetName': string;
    'rows': IRowApi[];
}

interface IUpdateSheetRequestApi extends IApi {
    '$type': "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
    TableName: string;
    SheetName: string;
    IsFirstRowHeader: true,
    comment: string | null;
    'Rows': IRowApi[];
}

interface IRequestBatchApi {
    '$type': "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal";
    fileBase64: string;
    requests: IReadSheetRequestApi[]
}




enum EViewState {
    export, import, results
}


export interface TLog {
    date: Date;
    type: 'warn' | 'error' | 'info' | 'ok';
    row: number | null;
    message: string;
}


type TValidationModel = IValidationModel<FAM.IGroupNumber[]>;

interface IValidatedTransaction {
    validation?: TValidationModel
}

interface IValidationData {

}


interface ISaveTransaction extends IValidatedTransaction {
    groupNumbers: FAM.IGroupNumber[],
    cesNumbers: FAM.ICESNumber[],
    departments: FAM.IDepartment[],
    metaData: Record<string, string>;
    logs: IValidationMessage[],
}

let templateId = 'ea90426a-f459-ec11-8f8f-000d3a5b6ed4';
templateId = '341719bb-ff93-ee11-be37-000d3a9980e7'

interface IViewModel {
    view: EViewState,
    importReady: boolean,
    exportReady: boolean,
    importGroupNumbers: boolean,
    importCESClientNumbers: boolean,
    importDepartments: boolean,
    exportDeactivated: boolean,
    hideInfoMessages: boolean,
    processing: boolean,
}

const GroupStructureImportWizard: React.FunctionComponent<IGroupStructureImportWizardProps> = (props) => {

    const { instance } = useMsal();
    const application = useApplication();
    const state = useSelector((state: RootState) => state);


    const dispatch = useDispatch();


    const [updateTransaction, setUpdateTransaction] = useState<ISaveTransaction | null>(null);
    const [importContacts, setImportContacts] = useState<IXrmTypes.contact[] | null>(null);


    // upload
    const [fileUpload, setFileUpload] = useState<string | null>(null);

    const clearCommandText = '(clear)';

    const [viewModel, setViewModel] = useState<IViewModel>({
        view: props.variant === 'export' ? EViewState.export : EViewState.import,
        importReady: false,
        exportReady: false,
        importGroupNumbers: true,
        importCESClientNumbers: true,
        importDepartments: true,
        exportDeactivated: false,
        hideInfoMessages: false,
        processing: false,
    });

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [prompts, setPrompts] = useState<IValidationMessage[]>([]);

    const canImport = state.status.viewType === EViewType.Modify;

    useEffect(() => {
        (async () => {
            const prompts: IValidationMessage[] = [];
            try {

                // CES Client Numbers
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet
                    const CESNumbers = ArrayToArrayByPredicateFlat(state.data.json.groupStructure.cesNumbers, t => t.cesNumber)
                        .filter(t => t.length > 1);

                    if (CESNumbers.length > 0)
                        CESNumbers.forEach(eachCESClientNumber => {
                            prompts.push({
                                title: `CES Client Number name (${eachCESClientNumber[0].cesNumber}) is not unique.`,
                                description: `The CES Client Numbers must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.\nYou can use Manage CES Client number to reconcile these CES numbers.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }


                // Plans
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet
                    const plans = ArrayToArrayByPredicateFlat(state.data.json.plans, t => t.name)
                        .filter(t => t.length > 1);

                    if (plans.length > 0)
                        plans.forEach(eachPlan => {
                            prompts.push({
                                title: `Plan name (${eachPlan[0].name}) is not unique.`,
                                description: `The names assigned to plans must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // Accounts Receivables
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet

                    const accountsReceivables = ArrayToArrayByPredicateFlat(state.data.json.claimsARs, t => t.name)
                        .filter(t => t.length > 1);


                    if (accountsReceivables.length > 0)
                        accountsReceivables.forEach(eachAccountsReceivables => {
                            prompts.push({
                                title: `Accounts Receivable name (${eachAccountsReceivables[0].name}) is not unique.`,
                                description: `The names assigned to Accounts Receivable must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // Stop Loss
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet
                    const stopLosses = ArrayToArrayByPredicateFlat(state.data.json.stopLoss, t => t.name)
                        .filter(t => t.length > 1);

                    if (stopLosses.length > 0)
                        stopLosses.forEach(eachStopLoss => {
                            prompts.push({
                                title: `Stop Loss name (${eachStopLoss[0].name}) is not unique.`,
                                description: `The names assigned to Stop Loss must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // Contacts
                // ---------------------------------------------
                {
                    // loading contacts for export
                    const contacts = await dataSource.getContactsForAccount(state.data.snapshot?._bcbs_groupid_value!, ['fullname', 'emailaddress1']);
                    setImportContacts(contacts);

                    // checking unique names, this is to ensure after export users would be able to pick
                    const contactNames = ArrayToArrayByPredicateFlat(contacts, t => t.fullname!)
                        .filter(t => t.length > 1);

                    if (contactNames.length > 0)
                        contactNames.forEach(eachContact => {
                            prompts.push({
                                title: `Contact (${eachContact[0].fullname}) is not unique.`,
                                description: `The names assigned to Stop Loss must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // get errors first
                prompts.sort((b, a) => { return b.type - a.type });

                setPrompts(t => ([...t, ...prompts]));

                setViewModel(vm => ({
                    ...vm,
                    exportReady: true,
                    importReady: true,
                }));

            } catch (error: any) {
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });
            }
            finally {
            }

        })();

    }, []);


    const friendlyContact = (contact: IXrmTypes.contact): string => {
        return `${contact.fullname} - ${contact.emailaddress1} - ${contact.contactid}`;
    }

    const onRenderExport = () => {

        return <Stack >
            <Separator />
            <Stack horizontal tokens={{ childrenGap: 15 }}>
                <img src={Excel} style={{ width: '60px' }} alt={"application icon"} />
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text block variant="small">
                        The process downloads Group Structure data (including CES Client Numbers, Group Numbers, Departments, Plans, Accounts Receivables, and Stop Losses) into an Excel file.
                    </Text>
                    <Text block variant="small">
                        Afterwards, needed updates can be made and the file can be re-uploaded.
                    </Text>
                </Stack>
            </Stack>
            <div style={{ marginTop: '18px' }}>

            </div>

            {viewModel.processing &&
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Spinner size={SpinnerSize.medium} />
                    <Text>Processing</Text>
                </Stack>
            }

            {viewModel.view === EViewState.export &&
                <Stack
                    tokens={{ childrenGap: 8 }}
                    styles={{ root: { marginTop: '15px', marginBottom: '10px' } }}
                >
                    <Label>Settings</Label>
                    <Checkbox
                        label="Process Group Numbers"
                        disabled={true}
                        checked={viewModel.importGroupNumbers}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, importGroupNumbers: checked ?? false })) }}
                    />
                    <Checkbox
                        label="Process CES Client Numbers"
                        disabled={true}
                        checked={viewModel.importCESClientNumbers}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, importCESClientNumbers: checked ?? false })) }}
                    />
                    <Checkbox
                        label="Process Departments"
                        disabled={true}
                        checked={viewModel.importDepartments}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, importDepartments: checked ?? false })) }}
                    />

                    <Checkbox
                        label="Exclude deactivated"
                        disabled={true}
                        checked={!viewModel.exportDeactivated}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, exportDeactivated: checked ?? false })) }}
                    />
                    <Separator />
                    <Stack>
                        {prompts.map((vm, index) => {
                            return <ValidationItem
                                key={index}
                                data={vm}
                            />;
                        })}
                    </Stack>
                </Stack>
            }


            {viewModel.view === EViewState.import &&

                <Stack
                    tokens={{ childrenGap: 8 }}
                    styles={{ root: { marginTop: '15px', marginBottom: '15px' } }}
                >
                    <FileImportDialog
                        maxFiles={1}
                        showAttached={true}
                        onDropBase64={(file) => {
                            setFileUpload(file);
                        }}
                    />

                    <Label>Settings</Label>
                    <Checkbox
                        label="Process Group Numbers"
                        disabled={true}
                        checked={viewModel.importGroupNumbers}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, importGroupNumbers: checked ?? false })) }}
                    />
                    <Checkbox
                        label="Process CES Client Numbers"
                        disabled={true}
                        checked={viewModel.importCESClientNumbers}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, importCESClientNumbers: checked ?? false })) }}
                    />
                    <Checkbox
                        label="Process Departments"
                        disabled={true}
                        checked={viewModel.importDepartments}
                        onChange={(event, checked) => { setViewModel(t => ({ ...t, importDepartments: checked ?? false })) }}
                    />
                </Stack>
            }

        </Stack>;
    }


    const onRenderExportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Download'
                styles={panelButtonsStyles}
                disabled={!viewModel.exportReady}
                onClick={async () => {

                    const logs: IValidationMessage[] = [];
                    try {

                        const fileB64 = await dataSource.crm.downloadFileAttributeBase64(
                            'bcbs_config_variables',
                            templateId,
                            'bcbs_attachment'
                        );

                        const requestJson = {
                            "$type": "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal",
                            "FileBase64": fileB64,
                            "ReadOnly": false,
                            "Requests": [
                            ]
                        } as any;


                        // Plans
                        // -------------------------------------------------------------------------------
                        const planList = state.data.json?.plans.filter(q => !q.deactivated) ?? [];

                        if (planList.length > 0) {
                            const rows = planList.map(q => {
                                return {
                                    "Plans": q.name
                                }
                            });

                            // rows.push({ 'Plans': clearCommandText });

                            requestJson.Requests.push({
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_Plan",
                                "SheetName": "Plans",
                                "IsFirstRowHeader": true,
                                "Rows": rows,
                                "comment": null
                            });
                        }
                        else {
                            logs.push({
                                date: new Date(),
                                title: `Configuration is missing Plans.`,
                                description: ``,
                                type: EValidationSeverity.warn
                            })
                        }

                        // CES Client Numbers
                        // -------------------------------------------------------------------------------
                        const cesList = state.data.json?.groupStructure?.cesNumbers.filter(q => !q.deactivated) ?? [];

                        if (cesList.length > 0)
                            requestJson.Requests.push({
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_CESClientNumbers",
                                "SheetName": "CES Client Numbers",
                                "IsFirstRowHeader": true,
                                "Rows": cesList.map(q => {
                                    return {
                                        "ID": q.id,
                                        "CES Client Number": q.cesNumber,
                                        "Status": q.deactivated ? "Deactivated" : "Active"
                                    }
                                }),
                                "comment": null
                            });
                        else {
                            logs.push({
                                date: new Date(),
                                title: `Configuration is missing CES Client Numbers.`,
                                description: ``,
                                type: EValidationSeverity.warn
                            })
                        }


                        // departments
                        // -------------------------------------------------------------------------------
                        const departmentList = state.data.json?.groupStructure?.departments.filter(q => !q.deactivated) ?? [];

                        if (departmentList.length > 0)
                            requestJson.Requests.push({
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_Departments",
                                "SheetName": "Departments",
                                "IsFirstRowHeader": true,
                                "Instructions": {
                                    "deleteRowsByIndex": "2"
                                },
                                "Rows": departmentList.map(q => {
                                    return {
                                        "ID": q.id,
                                        "CES Client Number": q.cesNumberRef?.name,
                                        "Department Number": q.name,
                                        "Description": q.description,
                                        "Status": q.deactivated ? "Deactivated" : "Active"
                                    }
                                }),
                                "comment": null
                            });
                        else {
                            logs.push({
                                date: new Date(),
                                title: `Configuration does not have any Departments.`,
                                description: ``,
                                type: EValidationSeverity.info
                            })
                        }


                        // Accounts Receivable
                        // -------------------------------------------------------------------------------
                        const accountsReceivableList = state.data.json?.claimsARs.filter(q => !q.deactivated) ?? [];

                        if (accountsReceivableList.length > 0) {

                            const rows = accountsReceivableList.map(q => {
                                return {
                                    "Accounts Receivable": q.name
                                }
                            });

                            // rows.push({ 'Accounts Receivable': clearCommandText });

                            requestJson.Requests.push({
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_AccountsReceivable",
                                "SheetName": "Accounts Receivable",
                                "IsFirstRowHeader": true,
                                "Rows": rows,
                                "comment": null
                            });
                        }
                        else {

                            logs.push({
                                date: new Date(),
                                title: `Configuration is missing Accounts Receivable.`,
                                description: ``,
                                type: EValidationSeverity.warn
                            })
                        }

                        // Stop Loss
                        // -------------------------------------------------------------------------------
                        const stopLossList = state.data.json?.stopLoss.filter(q => !q.deactivated) ?? [];

                        if (stopLossList.length > 0) {

                            const rows = stopLossList.map(q => {
                                return {
                                    "Stop Loss": q.name
                                }
                            });

                            // rows.push({ 'Stop Loss': clearCommandText });

                            requestJson.Requests.push({
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_Stop_Loss",
                                "SheetName": "Stop Loss",
                                "IsFirstRowHeader": true,
                                "Rows": rows,
                                "comment": null
                            });
                        }
                        else {
                            logs.push({
                                date: new Date(),
                                title: `Configuration is missing Stop Loss.`,
                                description: ``,
                                type: EValidationSeverity.warn
                            })
                        }

                        // Contacts
                        // -------------------------------------------------------------------------------
                        if (importContacts && importContacts.length > 0) {
                            const rows = importContacts.map(q => {
                                // if changed import has to change
                                return {
                                    "Contacts": friendlyContact(q)
                                };
                            });

                            // rows.push({ Contacts: clearCommandText });

                            requestJson.Requests.push({
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_Contacts",
                                "SheetName": "Contacts",
                                "IsFirstRowHeader": true,
                                "Rows": rows,
                                "comment": null
                            });
                        }
                        else {
                            logs.push({
                                date: new Date(),
                                title: `Configuration's account has not been associated with any contact.`,
                                description: ``,
                                type: EValidationSeverity.warn
                            })
                        }

                        // Group Numbers
                        // -------------------------------------------------------------------------------
                        if (state.data.json?.groupStructure?.groupNumbers?.length > 0) {
                            const updateSubgroupsRequest = {
                                "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                                "TableName": "Table_Subgroups",
                                "SheetName": "Import Template",
                                "IsFirstRowHeader": true,
                                "Rows": [] as IRowApi[],
                                "Instructions": {
                                    "deleteRowsByIndex": "2"
                                },
                                "comment": null
                            };

                            state.data.json.groupStructure.groupNumbers.forEach((eachGN, eachGNIndex) => {
                                const row: IRowApi = {
                                    '$type': "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib",
                                };

                                row['ID'] = eachGN.id;
                                row['Group Number'] = eachGN.groupNumber ?? ``;
                                row['Group Number'] = `<formula>:_xlfn.CONCAT(Table_Subgroups[[#This Row],[Master Prefix]],\"-\",Table_Subgroups[[#This Row],[Master Base]],\"-\",Table_Subgroups[[#This Row],[Suffix]])`
                                row['CES Client Number'] = eachGN.cesNumberRef?.name ?? ``;

                                // 12/05/2022
                                // After conversation with Sebrina T, it was decided to not exporting deactivated group numbers to avoid user confusion
                                if (eachGN.deactivated) {
                                    logs.push({
                                        date: new Date(),
                                        title: `Group Number (${eachGN.groupNumber}) is deactivated and has been skipped.`,
                                        description: ``,
                                        type: EValidationSeverity.info
                                    })

                                    return;
                                }

                                if (!eachGN.groupNumber)
                                    logs.push({
                                        date: new Date(),
                                        title: `Group Number (${eachGN.id}) is missing value for Group Number.`,
                                        description: ``,
                                        type: EValidationSeverity.warn
                                    })


                                if (!eachGN.cesNumberRef?.name)
                                    logs.push({
                                        date: new Date(),
                                        title: `Group Number (${eachGN.groupNumber}) is missing CES Client Number.`,
                                        description: ``,
                                        type: EValidationSeverity.warn
                                    })


                                // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/251035
                                if (eachGN.deactivated)
                                    if (eachGN.status?.value === FAM.EBenefitStatus["In-Progress"]) {
                                        logs.push({
                                            date: new Date(),
                                            title: `Group Number (${eachGN.groupNumber}) skip due to conflict on status and deactivated.`,
                                            description: ``,
                                            type: EValidationSeverity.warn
                                        })
                                        return;
                                    }

                                if (eachGN.prefix)
                                    row['Master Prefix'] = eachGN.prefix;

                                if (eachGN.base)
                                    row['Master Base'] = eachGN.base;

                                if (eachGN.suffix)
                                    row['Suffix'] = eachGN.suffix;

                                if (eachGN.prefix && eachGN.base) {

                                    row['Prefix-Base'] = `${eachGN.prefix}-${eachGN.base}`
                                    row['Prefix-Base'] = `<formula>:_xlfn.CONCAT(Table_Subgroups[[#This Row],[Master Prefix]],\"-\",Table_Subgroups[[#This Row],[Master Base]])`
                                }

                                if (eachGN.planRef?.name) {
                                    row['Plan'] = eachGN.planRef.name;
                                }

                                if (eachGN.accountsReceivableRef?.name) {
                                    row['Accounts Receivable'] = eachGN.accountsReceivableRef.name;
                                }

                                if (eachGN.stopLossRef?.name) {
                                    row['Stop Loss'] = eachGN.stopLossRef.name;
                                }

                                if (eachGN.deactivated) {
                                    row['Status'] = "Deactivated";
                                } else {
                                    if (eachGN.status?.value === FAM.EBenefitStatus.Cancelled)
                                        row['Status'] = "Cancelled";
                                    else
                                        row['Status'] = "Active";
                                }

                                eachGN.fields.forEach(eachField => {
                                    try {
                                        const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                                        const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];

                                        const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
                                        const ffValue = eachField.values[0].value;

                                        if (ffValue) {

                                            const name = attribute.bcbs_name!;
                                            let value = '';

                                            if (controlType === EControlType.lookup) {

                                                const ref = (ffValue as FAM.IEntityReferenceValue);

                                                if (ref.type === "entity" && ref.entityType === "contact") {

                                                    if (importContacts) {

                                                        const foundContact = importContacts.find(q => q.contactid === ref.id);

                                                        if (foundContact) {
                                                            value = friendlyContact(foundContact);
                                                        }
                                                    }
                                                }
                                            } else if (controlType === EControlType.date) {
                                                const ff = (ffValue as FAM.IDateValue);
                                                value = formatDate(parseDate(ff.value, 'local:yyyy-MM-dd'), 'local:MM/dd/yyyy');
                                            } else {
                                                value = friendlyValue(controlType, ffValue);
                                            }

                                            row[name] = value;
                                        }

                                    } catch (error) {
                                        logs.push({
                                            date: new Date(),
                                            title: `Group Number(${eachGN.groupNumber}), converting ${eachField.name}/${eachField.configRef.name} to flexible field value failed.`,
                                            description: ``,
                                            type: EValidationSeverity.warn
                                        })
                                    }

                                });

                                updateSubgroupsRequest.Rows.push(row);

                                // xLog.push({
                                //     date: new Date(),
                                //     title: `Group Number(${eachGN.groupNumber}) was processed.`,
                                //     description: ``,
                                //     type: EValidationSeverity.info
                                // })


                            });

                            if (updateSubgroupsRequest && updateSubgroupsRequest.Rows.length > 0)
                                requestJson.Requests.push(updateSubgroupsRequest);
                        }


                        // Meta Data
                        // -------------------------------------------------------------------------------
                        const metadata = [] as { Key: string, Value?: string, Description?: string }[];
                        metadata.push({
                            Key: `bcbs_name`,
                            Value: state.data.snapshot.bcbs_name,
                            Description: ''
                        })
                        metadata.push({
                            Key: `bcbs_blobversion`,
                            Value: state.data.snapshot.bcbs_blobversion,
                            Description: ''
                        })
                        metadata.push({
                            Key: `checksum`,
                            Value: cyrb53(JSON.stringify(state.data.json.groupStructure)).toLocaleString(),
                            Description: ''
                        })

                        // for (const [key, value] of Object.entries(state.data.snapshot)) {
                        //     metadata.push({
                        //         Key: key,
                        //         Value: value,
                        //         Description: ''
                        //     })
                        // }

                        requestJson.Requests.push({
                            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
                            "TableName": "Table_Metadata",
                            "SheetName": "Metadata",
                            "IsFirstRowHeader": true,
                            "Rows": metadata,
                            "comment": null
                        });


                        const { templateBlob, messages } = await dataSource.getTemplate(requestJson);


                        const fileName = `${state.data.json.bcbs_shortname ?? state.data.snapshot?.bcbs_groupconfigurationsnapshotid}-gs-${new Date().getTime()}.xlsx`;
                        saveAs(templateBlob, fileName);

                        if (canImport)
                            setViewModel(t => ({ ...t, view: EViewState.import }));


                        logs.push({
                            date: new Date(),
                            title: `Export complete.`,
                            type: EValidationSeverity.info,
                        });

                    } catch (error) {

                        application.QueueError({ error: error });

                    }
                    finally {
                        setPrompts(t => ([...t, ...logs]));
                    }

                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={() => { props.setView(EView.entry); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderImport = onRenderExport;

    const onRenderImportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Process'
                styles={panelButtonsStyles}
                disabled={fileUpload === null || !viewModel.importReady || !canImport}
                onClick={async () => {

                    const logs: IValidationMessage[] = []
                    try {

                        const reqJsonRead = {
                            "$type": "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal",
                            "FileBase64": fileUpload,
                            "ReadOnly": true,
                            "Requests": [
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Plans"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Import Template"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Accounts Receivable"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Departments"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "CES Client Numbers"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Metadata"
                                },
                            ]
                        }

                        const buildResp = await dataSource.net.raw('POST', 'api/Excel/Read', reqJsonRead);
                        const buildRespBlob: IRequestBatchApi = await buildResp.json();

                        // const plans = buildRespBlob.requests.find(q => q.sheetName === 'Plans');
                        // const accountsReceivable = buildRespBlob.requests.find(q => q.sheetName === 'Accounts Receivable');

                        const rGroupNumbers: FAM.IGroupNumber[] = [];
                        const rDepartments: FAM.IDepartment[] = [];
                        const rCESNumbers: FAM.ICESNumber[] = [];

                        // CES Client Number
                        // ----------------------
                        {
                            const xlCESClientNumbers = buildRespBlob.requests.find(q => q.sheetName === 'CES Client Numbers');

                            logs.push({
                                date: new Date(),
                                title: `${(xlCESClientNumbers?.rows.length ?? 1) - 1} CES Client Number lines detected.`,
                                type: EValidationSeverity.info,
                            });


                            xlCESClientNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                // skip first row, its the header
                                if (eachRowIndex === 0)
                                    return;

                                const rowCesClientNumber = eachRow['CES Client Number'];
                                const rowID = eachRow['ID'];
                                const rowStatus = eachRow['Status'];

                                const cesClientNumber: FAM.ICESNumber = {
                                    cesNumber: rowCesClientNumber,
                                    deactivated: rowStatus === "Active" ? false : true,
                                    id: rowID,
                                };

                                rCESNumbers.push(cesClientNumber)

                                logs.push({
                                    date: new Date(),
                                    title: `Row (${eachRowIndex}), CES Client Number (${cesClientNumber.cesNumber}) processed.`,
                                    type: EValidationSeverity.info,
                                });

                            })
                        }

                        // Group Number
                        // ----------------------
                        {
                            const xlGroupNumbers = buildRespBlob.requests.find(q => q.sheetName === 'Import Template');

                            logs.push({
                                date: new Date(),
                                title: `${(xlGroupNumbers?.rows.length ?? 1) - 1} Group Number lines detected.`,
                                type: EValidationSeverity.info,
                            });

                            xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                // skip first row, its the header
                                if (eachRowIndex === 0)
                                    return;

                                const rowCESClientNumber = eachRow['CES Client Number']?.trim();
                                const rowID = eachRow['ID'];
                                const rowGroupNumber = eachRow['Group Number'];
                                const rowMasterPrefix = eachRow['Master Prefix']?.trim();
                                const rowMasterBase = eachRow['Master Base']?.trim();
                                const rowSuffix = eachRow['Suffix']?.trim();

                                const rowPlan = eachRow['Plan'] === null ? null : eachRow['Plan']?.trim();
                                const rowAccountsReceivable = eachRow['Accounts Receivable'] === null ? null : eachRow['Accounts Receivable']?.trim();
                                const rowStopLoss = eachRow['Stop Loss'] === null ? null : eachRow['Stop Loss']?.trim();
                                const rowStatus = eachRow['Status'] === null ? null : eachRow['Status']?.trim();
                                const rowContact = eachRow['Contact'] === null ? null : eachRow['Contact']?.trim();
                                const rowGroupLeader = eachRow['Group Leader'] === null ? null : eachRow['Group Leader'];

                                let calcGroupNumber = rowGroupNumber;
                                if (!calcGroupNumber)
                                    if (rowMasterPrefix && rowMasterBase && rowSuffix)
                                        calcGroupNumber = `${rowMasterPrefix}-${rowMasterBase}-${rowSuffix}`;

                                // ok lets first search by the row id
                                let foundGN = state.data.json.groupStructure.groupNumbers.find(q => q.id === rowID);

                                let groupNumber = null as FAM.IGroupNumber | null;
                                // pick group number
                                if (foundGN) {
                                    groupNumber = structuredClone(foundGN);
                                } else {
                                    // group number for use if we are adding group number
                                    // -------------------------------------------------
                                    groupNumber = createGroupNumber(state.data);
                                };

                                if (rowID)
                                    groupNumber.id = rowID;

                                rGroupNumbers.push(groupNumber);

                                const cesNumberRef: FAM.IRef = { id: '', name: rowCESClientNumber };

                                // Properties
                                groupNumber.cesNumberRef = cesNumberRef
                                groupNumber.prefix = rowMasterPrefix;
                                groupNumber.suffix = rowSuffix;
                                groupNumber.base = rowMasterBase;
                                groupNumber.groupNumber = rowGroupNumber;

                                // Plan Mapping
                                // find matching element from model and create a reference
                                // --------------------------------------------------
                                if (rowPlan !== undefined)
                                    if (rowPlan === null || rowPlan.toLowerCase() === clearCommandText) {
                                        groupNumber.planRef = null
                                    } else {
                                        const t = state.data.json.plans.find(q => !q.deactivated && q.name.trim() === rowPlan)!;
                                        if (t) {
                                            groupNumber.planRef = {
                                                id: t.id,
                                                name: t.name
                                            }
                                        }
                                        else {
                                            logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), Plan (${rowPlan}) was not found.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }
                                    }

                                // Accounts Receivable Mapping
                                // find matching element from model and create a reference
                                // --------------------------------------------------
                                if (rowAccountsReceivable !== undefined)
                                    if (rowAccountsReceivable === null || rowAccountsReceivable.toLowerCase() === clearCommandText) {
                                        groupNumber.accountsReceivableRef = null;
                                    } else {
                                        const t = state.data.json.claimsARs.find(q => !q.deactivated && q.name.trim() === rowAccountsReceivable)!;
                                        if (t) {
                                            groupNumber.accountsReceivableRef = {
                                                id: t.id,
                                                name: t.name
                                            }
                                        }
                                        else {

                                            logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), Accounts Receivable (${rowAccountsReceivable}) was not found.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }
                                    }

                                // Accounts Receivable Mapping
                                // find matching element from model and create a reference
                                // --------------------------------------------------
                                if (rowStopLoss !== undefined)
                                    if (rowStopLoss === null || rowStopLoss.toLowerCase() === clearCommandText) {
                                        groupNumber.stopLossRef = null;
                                    }
                                    else {

                                        const t = state.data.json.stopLoss.find(q => !q.deactivated && q.name.trim() === rowStopLoss)!;

                                        if (t) {
                                            groupNumber.stopLossRef = {
                                                id: t.id,
                                                name: t.name
                                            }
                                        }
                                        else {

                                            logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), Stop Loss (${rowStopLoss}) was not found.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }

                                    }

                                // Status
                                // --------------------------------------------------
                                if (rowStatus)
                                    if (rowStatus === "Active") {

                                        groupNumber.deactivated = false;

                                        groupNumber.status = {
                                            name: FAM.EBenefitStatus[FAM.EBenefitStatus["In-Progress"]],
                                            type: "optionset",
                                            value: FAM.EBenefitStatus["In-Progress"]
                                        };

                                    } else if (rowStatus === "Cancelled") {

                                        groupNumber.deactivated = false

                                        groupNumber.status = {
                                            name: FAM.EBenefitStatus[FAM.EBenefitStatus["Cancelled"]],
                                            type: "optionset",
                                            value: FAM.EBenefitStatus["Cancelled"]
                                        };

                                    } else if (rowStatus === "Deactivated") {
                                        groupNumber.deactivated = true
                                    }
                                    else {

                                        logs.push({
                                            date: new Date(),
                                            title: `Row (${eachRowIndex}) skipped, Status did not have a matching value.`,
                                            type: EValidationSeverity.error
                                        });

                                        return;
                                    }

                                groupNumber.fields.forEach(eachField => {

                                    const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                                    const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];

                                    if (eachRow[attribute.bcbs_name!] !== undefined) {

                                        const valueText = eachRow[attribute.bcbs_name!];
                                        const control = masterAttribute.bcbs_attributetypecode as EControlType;

                                        try {

                                            //special ones
                                            let lookupTemplate = null as any;

                                            if (masterAttribute.bcbs_lookupentitylogicalname === "contact") {

                                                let thisContact: string | null = null;

                                                // Contact
                                                // bcbs_contactid
                                                // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=dc7c1bc6-d18d-ec11-b400-0022480a93ed
                                                if (attribute.bcbs_config_attributeid === 'dc7c1bc6-d18d-ec11-b400-0022480a93ed')
                                                    thisContact = rowContact;

                                                // Group Leader
                                                // bcbs_groupleaderid
                                                // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c07c1bc6-d18d-ec11-b400-0022480a93ed
                                                if (attribute.bcbs_config_attributeid === 'c07c1bc6-d18d-ec11-b400-0022480a93ed')
                                                    thisContact = rowGroupLeader;

                                                // Contacts
                                                // find matching element from model and create a reference
                                                // --------------------------------------------------
                                                if (thisContact !== "")
                                                    if (thisContact && importContacts) {

                                                        // let wsRegex = /^\s+|\s+$/g;
                                                        // const rowContactClean = thisContact.replaceAll(wsRegex, "");
                                                        // || (q.firstname ?? '' + q.middlename ?? '' + q.lastname ?? '') === thisContactClean

                                                        const contactId = thisContact.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g);

                                                        // lets first try extracting using just a contact id
                                                        let foundContact = importContacts.find(q => q.contactid === (contactId?.[0] ?? ''));

                                                        if (foundContact) {
                                                            lookupTemplate = {
                                                                type: "entity",
                                                                entityType: "contact",
                                                                id: foundContact.contactid,
                                                                name: foundContact.fullname
                                                            }
                                                        } else {
                                                            logs.push({
                                                                date: new Date(),
                                                                title: `Row (${eachRowIndex}), Contact (${rowContact}) was not found.`,
                                                                type: EValidationSeverity.warn
                                                            });
                                                        }
                                                    }
                                            }

                                            const value = friendlyValueToFlexibleFieldValue(
                                                state.data.config,
                                                control,
                                                valueText,
                                                attribute,
                                                masterAttribute,
                                                state.data.config.cache.map_bcbs_config_category_named["Group Number"],
                                                lookupTemplate
                                            );

                                            if (valueText === null || valueText === clearCommandText) {
                                                logs.push({
                                                    date: new Date(),
                                                    title: `Row (${eachRowIndex}), field (${attribute.bcbs_name}/${attribute.bcbs_config_attributeid}) was requested to be cleared.`,
                                                    type: EValidationSeverity.info
                                                });
                                            } else if (valueText === undefined) {
                                                throw new Error('failed to load value');
                                            }

                                            eachField.values[0].value = value;

                                        } catch (error) {

                                            logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), field (${attribute.bcbs_name}/${attribute.bcbs_config_attributeid}) mapping value failed.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }

                                    }
                                });

                                logs.push({
                                    date: new Date(),
                                    title: `Row (${eachRowIndex}), Group Number (${groupNumber.groupNumber}) processed.`,
                                    type: EValidationSeverity.info,
                                });

                            });
                        }


                        // Departments
                        // ----------------------
                        {
                            const xlDepartments = buildRespBlob.requests.find(q => q.sheetName === 'Departments');

                            logs.push({
                                date: new Date(),
                                title: `${(xlDepartments?.rows.length ?? 1) - 1} Department lines detected.`,
                                type: EValidationSeverity.info,
                            });

                            xlDepartments?.rows.forEach((eachRow, eachRowIndex) => {
                                // skip first row, its the header
                                if (eachRowIndex === 0)
                                    return;

                                const rowCesClientNumber = eachRow['CES Client Number'];
                                const rowID = eachRow['ID'];
                                const rowDescription = eachRow['Description'];
                                const rowDepartmentNumber = eachRow['Department Number'];
                                const rowStatus = eachRow['Status'];

                                let department = null as FAM.IDepartment | null;

                                department = {
                                    id: rowID,
                                    description: rowDescription,
                                    name: rowDepartmentNumber,
                                    deactivated: rowStatus === "Active" ? false : true,
                                    cesNumberRef: { id: '', name: rowCesClientNumber },
                                };

                                logs.push({
                                    date: new Date(),
                                    title: `Row (${eachRowIndex}), Department (${department.name}) processed.`,
                                    type: EValidationSeverity.info,
                                });

                                rDepartments.push(department);

                            });
                        }

                        // Metadata
                        const rMetadata: Record<string, string> = {}
                        {
                            const xlMetadata = buildRespBlob.requests.find(q => q.sheetName === 'Metadata');

                            xlMetadata?.rows.forEach(eachRow => {
                                const key = eachRow["Key"];
                                const value = eachRow["Value"];
                                const description = eachRow["Description"];

                                rMetadata[key] = value;
                                return [key, value, description]
                            })
                        }


                        // Transform and Reconciliations
                        // -----------------------------------------------------------------------
                        rCESNumbers.forEach(eachCESNumber => {

                            // no id? using alternate key to match, trying to not import duplicates
                            if (!eachCESNumber.id) {
                                const found = state.data.json.groupStructure.cesNumbers.find(t => t.cesNumber === eachCESNumber.cesNumber);

                                if (found) {
                                    eachCESNumber.id = found.id;
                                }
                            }

                            // This is new
                            if (!eachCESNumber.id) {
                                eachCESNumber.id = uuidv4();
                            }
                        })

                        rGroupNumbers.forEach(eachGroupNumber => {

                            // no id? using alternate key to match, trying to not import duplicates
                            if (!eachGroupNumber.id) {
                                const found = state.data.json.groupStructure.groupNumbers.find(t => t.groupNumber === eachGroupNumber.groupNumber);

                                if (found) {
                                    eachGroupNumber.id = found.id;
                                }
                            }

                            // This is new
                            if (!eachGroupNumber.id) {
                                eachGroupNumber.id = uuidv4();
                            }

                            if (eachGroupNumber.cesNumberRef?.name) {
                                const foundCESNumber = rCESNumbers.find(t => t.cesNumber === eachGroupNumber.cesNumberRef?.name);
                                if (foundCESNumber)
                                    eachGroupNumber.cesNumberRef = {
                                        id: foundCESNumber.id!,
                                        name: foundCESNumber.cesNumber!,
                                    };
                                else
                                    eachGroupNumber.cesNumberRef = null;
                            }
                        });

                        rDepartments.forEach(eachDepartment => {

                            // no id? using alternate key to match, trying to not import duplicates
                            if (!eachDepartment.id) {
                                const found = state.data.json.groupStructure.departments.find(t => t.name === eachDepartment.name);

                                if (found) {
                                    eachDepartment.id = found.id;
                                }
                            }

                            // This is new
                            if (!eachDepartment.id) {
                                eachDepartment.id = uuidv4();
                            }

                            if (eachDepartment.cesNumberRef?.name) {
                                const foundCESNumber = rCESNumbers.find(t => t.cesNumber === eachDepartment.cesNumberRef?.name);
                                if (foundCESNumber)
                                    eachDepartment.cesNumberRef = {
                                        id: foundCESNumber.id!,
                                        name: foundCESNumber.cesNumber!,
                                    };
                                else
                                    eachDepartment.cesNumberRef = null;

                            }
                        });

                        logs.push({
                            date: new Date(),
                            title: `Import complete.`,
                            type: EValidationSeverity.info,
                        });


                        // Post processing, map ces client number
                        updateGroupConfig({
                            groupNumbers: rGroupNumbers,
                            departments: rDepartments,
                            cesNumbers: rCESNumbers,
                            metaData: rMetadata,
                            logs: logs,
                        });

                        setPrompts(t => ([...t, ...logs]));


                    } catch (error) {
                        application.QueueError({ error: error });
                    } finally {
                        setViewModel(vm => ({ ...vm, processing: false }));
                    }
                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={() => { props.setView(EView.entry); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }


    const onRenderResults = () => {
        const clicker = <Link onClick={(event) => {

            const csvSanitize = (input: string) => {
                const escapedStr = `"${input.replace(/"/g, '""')}"`;
                return escapedStr;
            }

            const messages = prompts.map(q => {
                return [
                    q.date?.toISOString(),
                    q.type,
                    csvSanitize(q.title),
                    csvSanitize(q.description ?? "")
                ].join(',')
            });

            if (messages[0] !== 'date,type,title,description')
                messages.unshift('date,type,title,description');

            const fileName = `${state.data.json.bcbs_shortname ?? state.data.snapshot?.bcbs_groupconfigurationsnapshotid}-gs-${new Date().getTime()}.log.csv`;
            const blob = new Blob([messages.join(`\r\n`)], { type: 'text/plan;charset=utf-8' });
            saveAs(blob, fileName, { autoBom: true });
        }}>
            here
        </Link>;

        return <Stack tokens={{ childrenGap: 15 }}>
            <Separator />
            <Checkbox
                label="Hide information messages"
                checked={viewModel.hideInfoMessages}
                onChange={(event, checked) => setViewModel(t => ({ ...t, hideInfoMessages: checked ?? false }))}
            />
            <div>
                <Text>Click&nbsp;{clicker}&nbsp;to download log details.</Text>
            </div>


            <Stack
                tokens={{ childrenGap: 8 }}
                styles={{ root: { marginTop: '15px', marginBottom: '15px' } }}
            >
                <Stack>
                    {prompts.map((vm, index) => {
                        if (viewModel.hideInfoMessages)
                            if (vm.type === EValidationSeverity.info)
                                return <></>;

                        return <ValidationItem
                            key={index}
                            data={vm}
                        />;
                    })}
                </Stack>
            </Stack>
        </Stack>
    }

    const onRenderResultsFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Done'
                styles={panelButtonsStyles}
                onClick={async () => {
                    props.onDismiss?.();
                }}
            />
            <DefaultButton
                text='Cancel'
                disabled
                onClick={() => {
                    props.onDismiss?.();
                }}
            />
        </div>;
    }

    const updateGroupConfig = async (xSaveModel: ISaveTransaction) => {
        let saveModel: ISaveTransaction | null = { ...xSaveModel };

        const saveModelLogs = saveModel?.logs;

        try {

            // create validation if it does not already exists
            if (!saveModel.validation) {
                saveModel.validation = {
                    validationMessages: [],
                    state: EValidationState.PendingUser,
                    data: xSaveModel.validation?.data,
                }

                const validations = saveModel.validation?.validationMessages!;
                {
                    const { groupNumbers, cesNumbers, departments, metaData } = saveModel;

                    // const stateCESNumberById = ArrayToLookupByPredicate(state.data.json.groupStructure.cesNumbers, t => t.id);
                    // const stateGroupNumbersById = ArrayToLookupByPredicate(state.data.json.groupStructure.groupNumbers, t => t.id);
                    // const stateDepartmentsById = ArrayToLookupByPredicate(state.data.json.groupStructure.departments, t => t.id);

                    // CES Client Numbers
                    // -----------------------
                    {
                        const cesClientNumberByCESNumberTest: Record<string, FAM.ICESNumber[]> = {};
                        const cesClientNumberByIdTest: Record<string, FAM.ICESNumber[]> = {};

                        cesNumbers.forEach(eachCESNumber => {

                            if (!eachCESNumber.deactivated) {
                                if (!eachCESNumber.id) {
                                    validations.push({
                                        title: `CES Client Number (${eachCESNumber.cesNumber}) is missing ID.`,
                                        description: `This CES Client Number ID has been specified multiple times in Group Structure\nYou may need to change CES Number or deactivate one(s) that are no longer active.`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }

                            if (!eachCESNumber.deactivated)
                                if (!eachCESNumber.cesNumber) {
                                    validations.push({
                                        title: `CES Client Number with ID (${eachCESNumber.id}) is missing CES Number.`,
                                        type: EValidationSeverity.error,
                                    });
                                }


                            if (!eachCESNumber.deactivated) {
                                if (eachCESNumber.cesNumber) {

                                    cesClientNumberByCESNumberTest[eachCESNumber.cesNumber] = cesClientNumberByCESNumberTest[eachCESNumber.cesNumber] ?? [];
                                    cesClientNumberByCESNumberTest[eachCESNumber.cesNumber].push(eachCESNumber);

                                    if (cesClientNumberByCESNumberTest[eachCESNumber.cesNumber].length > 1)
                                        validations.push({
                                            title: `CES Client Number (${eachCESNumber.cesNumber}) is not unique.`,
                                            description: `This CES Client Number has been specified multiple times in Group Structure\nYou may need to change CES Number or deactivate one(s) that are no longer active.`,
                                            type: EValidationSeverity.error,
                                        });
                                }
                            }


                            if (!eachCESNumber.deactivated) {
                                if (eachCESNumber.id) {

                                    cesClientNumberByIdTest[eachCESNumber.id] = cesClientNumberByIdTest[eachCESNumber.id] ?? [];
                                    cesClientNumberByIdTest[eachCESNumber.id].push(eachCESNumber);

                                    if (cesClientNumberByIdTest[eachCESNumber.id].length > 1)
                                        validations.push({
                                            title: `CES Client Number (${eachCESNumber.cesNumber}) has a duplicated ID (${eachCESNumber.id}).`,
                                            description: `This CES Client Number ID has been specified multiple times in Group Structure\nYou may need to change CES Number or deactivate one(s) that are no longer active.`,
                                            type: EValidationSeverity.error,
                                        });
                                }
                            }


                        });
                    }

                    // Group Numbers
                    // -----------------------
                    {
                        const groupNumberByGroupNumberTest: Record<string, FAM.IGroupNumber[]> = {};
                        const groupNumberByIdTest: Record<string, FAM.IGroupNumber[]> = {};

                        groupNumbers.forEach(eachGroupNumber => {
                            if (!eachGroupNumber.deactivated)
                                if (!eachGroupNumber.id)
                                    validations.push({
                                        title: `Group Number (${eachGroupNumber.groupNumber}) is missing ID.`,
                                        type: EValidationSeverity.error,
                                    });


                            // is Group Number set ?
                            if (!eachGroupNumber.deactivated)
                                if (!eachGroupNumber.groupNumber)
                                    validations.push({
                                        title: `Group Number with ID (${eachGroupNumber.id}) is missing Group Number.`,
                                        type: EValidationSeverity.error,
                                    });

                            // Making sure Department ids are unique
                            if (!eachGroupNumber.deactivated)
                                if (eachGroupNumber.id) {
                                    groupNumberByIdTest[eachGroupNumber.id] = groupNumberByIdTest[eachGroupNumber.id] ?? [];
                                    groupNumberByIdTest[eachGroupNumber.id].push(eachGroupNumber);

                                    if (groupNumberByIdTest[eachGroupNumber.id].length > 1)
                                        validations.push({
                                            title: `Group Number (${eachGroupNumber.groupNumber}) has a duplicated ID (${eachGroupNumber.id}).`,
                                            type: EValidationSeverity.error,
                                        });
                                }

                            // is Group Number unique ?
                            if (!eachGroupNumber.deactivated)
                                if (eachGroupNumber.groupNumber) {

                                    groupNumberByGroupNumberTest[eachGroupNumber.groupNumber] = groupNumberByGroupNumberTest[eachGroupNumber.groupNumber] ?? [];
                                    groupNumberByGroupNumberTest[eachGroupNumber.groupNumber].push(eachGroupNumber);

                                    if (groupNumberByGroupNumberTest[eachGroupNumber.groupNumber].length > 1)
                                        validations.push({
                                            title: `Group Number (${eachGroupNumber.groupNumber}) is not unique.`,
                                            description: `This Group Number has been used multiple times in Group Structure\nYou may need to change group number or deactivate one(s) that are no longer active.`,
                                            type: EValidationSeverity.error,
                                        });
                                }

                            // Verify Group Number -> CES Client number exist prior to importing
                            if (!eachGroupNumber.deactivated) {
                                const cesNumberFound = cesNumbers.find(t => t.cesNumber === eachGroupNumber.cesNumberRef?.name);
                                if (!cesNumberFound) {
                                    validations.push({
                                        title: `Group Number (${eachGroupNumber.groupNumber}) is referencing an invalid CES Number (${eachGroupNumber.cesNumberRef?.name}).`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }
                        });
                    }

                    // Departments
                    // -----------------------
                    {
                        const departmentByIdTest: Record<string, FAM.IDepartment[]> = {};
                        departments.forEach(eachDepartment => {

                            // is department valid?
                            if (!eachDepartment.deactivated)
                                if (!eachDepartment.id)
                                    validations.push({
                                        title: `Department (${eachDepartment.name}) is missing ID.`,
                                        type: EValidationSeverity.error,
                                    });

                            // is department valid?
                            if (!eachDepartment.deactivated)
                                if (!eachDepartment.name)
                                    validations.push({
                                        title: `Department with ID (${eachDepartment.id}) is missing Department Number.`,
                                        type: EValidationSeverity.error,
                                    });

                            // Making sure Department ids are unique
                            if (!eachDepartment.deactivated)
                                if (eachDepartment.id) {

                                    departmentByIdTest[eachDepartment.id] = departmentByIdTest[eachDepartment.id] ?? [];

                                    departmentByIdTest[eachDepartment.id].push(eachDepartment);

                                    if (departmentByIdTest[eachDepartment.id].length > 1)
                                        validations.push({
                                            title: `Department (${eachDepartment.name}) has a duplicated ID (${eachDepartment.id}).`,
                                            type: EValidationSeverity.error,
                                        });
                                }

                            //  Verify Department -> CES Client number exist prior to importing
                            if (!eachDepartment.deactivated) {
                                const cesNumberFound = cesNumbers.find(t => t.cesNumber === eachDepartment.cesNumberRef?.name);
                                if (!cesNumberFound) {
                                    validations.push({
                                        title: `Department (${eachDepartment.name}) is referencing an invalid CES Number (${eachDepartment.cesNumberRef?.name}).`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }
                        });
                    }

                    // Metadata
                    // -----------------------
                    {
                        if (metaData.checksum) {

                            const checksum = cyrb53(JSON.stringify(state.data.json.groupStructure)).toLocaleString();
                            if (metaData.checksum !== checksum) {
                                validations.push({
                                    title: `Group structure checksum discrepancy was detected. (Import file/Source Data)`,
                                    description: `During the export process, we generated a checksum that currently does not align with the existing group structure data. This discrepancy might be a result of modifications to the data or a subsequent import operation that altered the data.\n\nConsequently, the current import file might potentially become a source for data duplication. Therefore, we recommend regenerating the export file and applying your updates to ensure data integrity and prevent duplication.`,
                                    type: EValidationSeverity.warn,
                                });
                            }
                        }
                    }
                }

                // validation rules
                if (saveModel.validation.validationMessages.length > 0) {
                    saveModel.validation.state = EValidationState.PendingUser;
                    return;
                }
                else {
                    saveModel.validation.state = EValidationState.Fulfilled;
                }
            }

            if (saveModel.validation && saveModel.validation.state !== EValidationState.Fulfilled)
                return;


            dispatch(addUpdateGroupStructure({
                groupNumbers: saveModel.groupNumbers,
                cesNumbers: saveModel.cesNumbers,
                departments: saveModel.departments,
            }));

            setPrompts(t => ([...t, ...saveModelLogs]));
            setFileUpload(null);

            setViewModel(t => ({ ...t, view: EViewState.results }));

            saveModel = null;

        } finally {
            setUpdateTransaction(saveModel);
        }
    }

    const onRenderValidationDialog = () => {
        if (updateTransaction?.validation?.state === EValidationState.PendingUser)
            return <ValidationDialog
                defaultValue={updateTransaction?.validation}
                onCancel={(xValidationModel) => {
                    setUpdateTransaction(null);
                }}
                onContinue={(xValidationModel) => {
                    const nSaveTransaction: ISaveTransaction = {
                        ...updateTransaction!,
                        validation: {
                            ...updateTransaction.validation!,
                            state: EValidationState.Fulfilled
                        }
                    }
                    updateGroupConfig(nSaveTransaction);
                }}
            />
    }


    const onRenderBody = () => {
        let onRender: (() => JSX.Element) | null = null;

        switch (viewModel.view) {
            case EViewState.export:
                onRender = onRenderExport;
                break;

            case EViewState.import:
                onRender = onRenderImport;
                break;

            case EViewState.results:
                onRender = onRenderResults;
                break;
        }

        if (!onRender)
            throw new Error("unexpected view state.");

        return <>
            {onRender()}
            {onRenderValidationDialog()}
        </>
    }

    const onRenderFooter = () => {

        switch (viewModel.view) {
            case EViewState.export:
                return onRenderExportFooter();

            case EViewState.import:
                return onRenderImportFooter();

            case EViewState.results:
                return onRenderResultsFooter();

            default:
                throw new Error("unexpected view state.");
        }
    }


    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        layerProps={{ eventBubblingEnabled: true }}
        headerText={props.variant === "export" ? "Group Structure Export" : "Group Structure Import"}
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>
}

export default GroupStructureImportWizard;